import React, { useEffect, useState, useMemo, useCallback } from "react";
import {
  Box,
  Button,
  Chip,
  Divider,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Radio,
  Select,
  TextField,
  Typography,
  IconButton,
  Autocomplete,
  Switch,
  Alert,
  Tooltip,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import AccessAlarmsRoundedIcon from "@mui/icons-material/AccessAlarmsRounded";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { Controller, useFieldArray, useForm } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import dayjs from "dayjs";
import { motion } from "framer-motion";
import { useDialog } from "../../../../components/DialogContext";
import {
  createTaskTemplate,
  getAllTask,
  getAllTaskTemplate,
  updateTaskOrType,
} from "../../../../redux/Apps/Lawyerapp/TaskSlice/TaskSlice";
import { getPracticeArea } from "../../../../redux/Apps/Lawyerapp/CaseSlice/CaseSlice";
import { backend_api } from "../../../../static/server";

const CreateTaskTemplateForm = ({
  isEditMode,
  editTaskId,
  formData,
  onSuccess,
}) => {
  const { closeDialog } = useDialog();
  const dispatch = useDispatch();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selected, setSelected] = useState("is_practice_area");
  const [isInitialized, setIsInitialized] = useState(false);

  const { token, teamMemberLawyerId } = useSelector(
    (state) => state.user
  );
  const { allPracticeArea } = useSelector((state) => state.case);

  const {
    control,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { errors },
  } = useForm({
    defaultValues: {
      task_name: "",
      priority: "normal",
      comment: "",
      status: "Open",
      task_alarm: [],
      private_task: false,
      is_practice_area: true,
      is_people: false,
      is_company: false,
      practice_area: null,
    },
  });

  const { fields, append, remove, replace } = useFieldArray({
    control,
    name: "task_alarm",
    keyName: "id",
  });

  useEffect(() => {
    dispatch(
      getAllTask({
        url: `${backend_api}task/task-template/`,
        token,
        teamMemberLawyerId: teamMemberLawyerId,
      })
    );
    dispatch(
      getPracticeArea({
        url: `${backend_api}case/case-type/?case_service_type=practice_area`,
        token: token,
      })
    );
  }, [dispatch, token, teamMemberLawyerId]);

  // Memoized function to process alarm data
  const processAlarmData = useCallback((alarm) => {
    let alarmData = {
      via: alarm.via || "email",
      days: 0,
      hours: 0,
      minutes: 0,
      exactness: alarm.exactness || "non-exact",
    };

    if (alarm.alram_at) {
      const alarmDate = dayjs(alarm.alram_at);
      const now = dayjs();
      const diffInMinutes = alarmDate.diff(now, "minute");

      if (diffInMinutes > 0) {
        const days = Math.floor(diffInMinutes / (24 * 60));
        const hours = Math.floor((diffInMinutes % (24 * 60)) / 60);
        const minutes = diffInMinutes % 60;

        alarmData.days = days;
        alarmData.hours = hours;
        alarmData.minutes = minutes;
      }
    } else {
      alarmData.days = parseInt(alarm.days) || 0;
      alarmData.hours = parseInt(alarm.hours) || 0;
      alarmData.minutes = parseInt(alarm.minutes) || 0;
    }

    return alarmData;
  }, []);

  useEffect(() => {
    if (!isEditMode || !formData || isInitialized) return;

    const data = formData?.full_data || formData;
    // console.log("Edit mode data:", data);

    // Basic fields
    setValue("task_name", data?.task_name || data?.template_name || "");
    setValue("priority", data?.priority?.toLowerCase() || "normal");
    setValue("comment", data?.comment || data?.description || "");
    setValue("status", data?.status || "Open");
    setValue("private_task", Boolean(data?.private_task));

    // Handle radio button selection
    let selectedOption = "is_practice_area";

    if (data?.is_practice_area === true) {
      selectedOption = "is_practice_area";
    } else if (data?.is_people === true) {
      selectedOption = "is_people";
    } else if (data?.is_company === true) {
      selectedOption = "is_company";
    }

    setSelected(selectedOption);
    setValue("is_practice_area", selectedOption === "is_practice_area");
    setValue("is_people", selectedOption === "is_people");
    setValue("is_company", selectedOption === "is_company");

    if (data?.practice_area && selectedOption === "is_practice_area") {
      const practiceAreaId =
        typeof data.practice_area === "object"
          ? data.practice_area.id
          : data.practice_area;

      const practiceAreaObj = allPracticeArea?.find(
        (area) => area.id === practiceAreaId
      );
      if (practiceAreaObj) {
        setValue("practice_area", practiceAreaObj);
      }
    } else {
      setValue("practice_area", null);
    }

    if (Array.isArray(data?.task_alarm) && data.task_alarm.length > 0) {
      const processedAlarms = data.task_alarm.map(processAlarmData);
      replace(processedAlarms);
    } else {
      replace([]);
    }

    setIsInitialized(true);

    return () => {
      setIsInitialized(false);
    };
  }, [
    allPracticeArea,
    formData,
    isEditMode,
    isInitialized,
    processAlarmData,
    replace,
    setValue,
  ]);

  const handleChange = useCallback(
    (event) => {
      const value = event.target.value;
      setSelected(value);

      // Update form values based on selection - ensure only one is true
      setValue("is_practice_area", value === "is_practice_area");
      setValue("is_people", value === "is_people");
      setValue("is_company", value === "is_company");

      // Reset practice area field when changing selection
      if (value !== "is_practice_area") {
        setValue("practice_area", null);
      }
    },
    [setValue]
  );

  const onSubmit = async (data) => {
    setIsSubmitting(true);

    // Ensure alarm data has proper integer values and all required keys
    const processedAlarms = data.task_alarm.map((alarm) => ({
      via: alarm.via || "email",
      days: parseInt(alarm.days) || 0,
      hours: parseInt(alarm.hours) || 0,
      minutes: parseInt(alarm.minutes) || 0,
      exactness: alarm.exactness || "non-exact",
    }));

    // Prepare the payload with the correct dependency flags
    const payload = {
      task_name: data.task_name,
      priority: data.priority,
      comment: data.comment,
      status: data.status,
      task_alarm: processedAlarms,
      private_task: Boolean(data.private_task),
      is_practice_area: selected === "is_practice_area",
      is_people: selected === "is_people",
      is_company: selected === "is_company",
      is_dependent: true, // Always true for templates
    };

    // Add practice_area only if it's selected
    if (selected === "is_practice_area" && data.practice_area) {
      payload.practice_area = data.practice_area.id || data.practice_area;
    } else {
      payload.practice_area = null;
    }

    // console.log("Submitting payload:", payload);

    try {
      let response;
      if (isEditMode) {
        response = await dispatch(
          updateTaskOrType({
            url: `${backend_api}task/task-template/${editTaskId}/`,
            body: payload,
            token,
          })
        ).unwrap();
        toast.success("Task template updated successfully");
      } else {
        response = await dispatch(
          createTaskTemplate({
            url: `${backend_api}task/task-template/`,
            body: payload,
            token,
            teamMemberLawyerId,
          })
        ).unwrap();
        toast.success("Task template created successfully!");
      }

      if (onSuccess && response) onSuccess(response);
      dispatch(
        getAllTask({
          url: `${backend_api}task/task-template/`,
          token,
          teamMemberLawyerId: teamMemberLawyerId,
        })
      );

      closeDialog();
      reset();
    } catch (error) {
      console.error("Submit error:", error);
      toast.error(
        error?.message ||
          (isEditMode
            ? "Failed to update task template"
            : "Failed to create task template")
      );
    } finally {
      setIsSubmitting(false);
      dispatch(
        getAllTaskTemplate({
          url: `${backend_api}task/task-template/`,
          token,
          teamMemberLawyerId: teamMemberLawyerId,
        })
      );
    }
  };

  const handleAddAlarm = useCallback(() => {
    append({
      via: "email",
      days: 0,
      hours: 0,
      minutes: 0,
      exactness: "non-exact",
    });
  }, [append]);

  const getCurrentPracticeArea = useCallback(() => {
    const currentValue = watch("practice_area");
    return currentValue || null;
  }, [watch]);

  // Memoized helper function to update specific alarm field
  const updateAlarmField = useCallback(
    (index, field, value) => {
      const currentAlarm = watch(`task_alarm.${index}`) || {};
      setValue(`task_alarm.${index}`, {
        ...currentAlarm,
        [field]: field === "exactness" ? value : parseInt(value) || 0,
      });
    },
    [watch, setValue]
  );

  // Memoized helper function to calculate reminder example
  const getReminderExample = useCallback(
    (days, hours, minutes, exactness, templateType, practiceArea) => {
      const now = dayjs();
      const totalMinutes = days * 24 * 60 + hours * 60 + minutes;

      if (totalMinutes === 0) {
        return "Reminder will be sent immediately when the matter is created";
      }

      const reminderDate = now.add(totalMinutes, "minute");
      const templateTypeName =
        templateType === "is_practice_area"
          ? `practice area "${
              practiceArea?.case_type_name || practiceArea?.name || "selected"
            }"`
          : templateType === "is_people"
          ? "people"
          : "company";

      if (exactness === "exact") {
        return `Reminder will be sent on ${reminderDate.format(
          "MMM DD, YYYY"
        )} at ${reminderDate.format(
          "hh:mm A"
        )} after creating matter in ${templateTypeName}`;
      } else {
        return `Reminder will be sent on ${reminderDate.format(
          "MMM DD, YYYY"
        )} at 12:00 AM after creating matter in ${templateTypeName}`;
      }
    },
    []
  );

  // Memoized component for reminder items to prevent unnecessary re-renders
  const ReminderItem = useMemo(
    () =>
      ({ field, index, remove, selected, watch }) => {
        const currentAlarm = watch(`task_alarm.${index}`) || field;
        const currentPracticeArea = watch("practice_area");
        const reminderExample = getReminderExample(
          currentAlarm.days || 0,
          currentAlarm.hours || 0,
          currentAlarm.minutes || 0,
          currentAlarm.exactness || "non-exact",
          selected,
          currentPracticeArea
        );

        return (
          <Box
            key={field.id}
            sx={{
              border: "0.0625rem solid #e0e0e0",
              borderRadius: "0.75rem",
              backgroundColor: "background.paper",
              boxShadow: "0 0.125rem 0.25rem rgba(0,0,0,0.08)",
            }}
          >
            {/* Controls Row */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                p: "1rem",
                gap: "1rem",
                flexWrap: "wrap",
              }}
            >
              {/* Via */}
              <Controller
                name={`task_alarm.${index}.via`}
                control={control}
                defaultValue={field.via || "email"}
                render={({ field: viaField }) => (
                  <Select {...viaField} size="small" sx={{ minWidth: "9rem" }}>
                    <MenuItem value="email">Me (via Email)</MenuItem>
                    <MenuItem value="notification">
                      Me (via Notification)
                    </MenuItem>
                  </Select>
                )}
              />

              <Typography variant="body2" color="text.secondary">
                after
              </Typography>

              {/* Time Inputs */}
              <Box
                sx={{
                  display: "flex",
                  gap: "0.75rem",
                  flexWrap: "wrap",
                  alignItems: "center",
                }}
              >
                {["days", "hours", "minutes"].map((unit) => {
                  const defaults = { days: 0, hours: 0, minutes: 0 };
                  const limits = {
                    days: { min: 0 },
                    hours: { min: 0, max: 23 },
                    minutes: { min: 0, max: 59 },
                  };

                  return (
                    <Controller
                      key={unit}
                      name={`task_alarm.${index}.${unit}`}
                      control={control}
                      defaultValue={field[unit] ?? defaults[unit]}
                      render={({ field: inputField }) => (
                        <TextField
                          type="number"
                          size="small"
                          label={unit.charAt(0).toUpperCase() + unit.slice(1)}
                          sx={{ width: "6rem" }}
                          inputProps={{
                            ...limits[unit],
                            step: 1,
                            inputMode: "numeric",
                          }}
                          value={
                            inputField.value === "" ||
                            inputField.value === undefined
                              ? ""
                              : String(inputField.value)
                          }
                          onChange={(e) => {
                            const raw = e.target.value;

                            if (raw === "") {
                              inputField.onChange("");
                              return;
                            }

                            let cleaned = String(raw).replace(/\D/g, "");
                            cleaned = cleaned.replace(/^0+/, "");
                            if (cleaned === "") cleaned = "0";

                            let num = parseInt(cleaned, 10);

                            if (limits[unit].max !== undefined) {
                              num = Math.min(
                                Math.max(num, limits[unit].min),
                                limits[unit].max
                              );
                            } else if (num < limits[unit].min) {
                              num = limits[unit].min;
                            }

                            inputField.onChange(num);
                            updateAlarmField(index, unit, num);
                          }}
                        />
                      )}
                    />
                  );
                })}
              </Box>

              {/* Exact Switch */}
              <Controller
                name={`task_alarm.${index}.exactness`}
                control={control}
                defaultValue={field.exactness || "non-exact"}
                render={({ field: exactField }) => (
                  <FormControlLabel
                    control={
                      <Switch
                        size="small"
                        checked={exactField.value === "exact"}
                        onChange={(e) => {
                          const newValue = e.target.checked
                            ? "exact"
                            : "non-exact";
                          exactField.onChange(newValue);
                          updateAlarmField(index, "exactness", newValue);
                        }}
                      />
                    }
                    label="Exact Time"
                    sx={{
                      ml: "auto",
                      "& .MuiFormControlLabel-label": {
                        fontSize: "0.75rem",
                        color: "text.secondary",
                      },
                    }}
                  />
                )}
              />

              {/* Remove Button */}
              <IconButton
                onClick={() => remove(index)}
                size="small"
                sx={{
                  color: "error.main",
                  "&:hover": {
                    backgroundColor: "error.light",
                    color: "white",
                  },
                }}
              >
                <CloseRoundedIcon fontSize="small" />
              </IconButton>
            </Box>

            {/* Example Alert */}
            <Alert
              severity="info"
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                borderRadius: "0.5rem",
                backgroundColor: "rgba(25, 118, 210, 0.04)",
                "& .MuiAlert-icon": { fontSize: "1rem" },
              }}
            >
              <Typography variant="caption" sx={{ fontSize: "0.75rem" }}>
                <strong>Example:</strong> {reminderExample}
              </Typography>
            </Alert>
          </Box>
        );
      },
    [control, getReminderExample, updateAlarmField]
  );

  return (
    <Box>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: "1rem",
            p: "1rem",
          }}
        >
          {/* Task Template Name and Priority */}
          <Box sx={{ display: "flex", gap: "1rem" }}>
            <Box sx={{ flex: 1 }}>
              <Controller
                name="task_name"
                control={control}
                rules={{ required: "Task template name is required" }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    size="small"
                    label={
                      <span>
                        Task Template Name{" "}
                        <span style={{ color: "red" }}>*</span>
                      </span>
                    }
                    fullWidth
                    error={Boolean(errors.task_name)}
                    helperText={errors.task_name?.message}
                  />
                )}
              />
            </Box>
            <Box sx={{ flex: 0.5 }}>
              <Controller
                name="priority"
                control={control}
                render={({ field }) => (
                  <FormControl size="small" fullWidth>
                    <InputLabel id="priority-label">Priority</InputLabel>
                    <Select
                      {...field}
                      labelId="priority-label"
                      label="Priority"
                    >
                      <MenuItem value="low">Low</MenuItem>
                      <MenuItem value="normal">Normal</MenuItem>
                      <MenuItem value="high">High</MenuItem>
                    </Select>
                  </FormControl>
                )}
              />
            </Box>
          </Box>

          <Box sx={{ display: "flex", gap: "1rem" }}>
            <Box sx={{ flex: 1 }}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  border: "1px solid #c5c5c7",
                  borderRadius: "0.5rem",
                  px: "0.75rem",
                  py: "0.25rem",
                  minHeight: "2.5rem",
                }}
              >
                <Box>
                  <Typography variant="body2" fontWeight={600}>
                    {watch("private_task") ? "Private Template" : "Firm Template"}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Private templates stay visible only to the creator.
                  </Typography>
                </Box>
                <Controller
                  name="private_task"
                  control={control}
                  render={({ field: { onChange, value } }) => (
                    <Switch
                      checked={Boolean(value)}
                      onChange={(e) => onChange(e.target.checked)}
                    />
                  )}
                />
              </Box>
            </Box>
          </Box>

          {/* Relates to Selection */}
          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
              Depend On
            </Typography>
            <Box
              sx={{
                display: "flex",
                justifyContent: "start",
                alignItems: "center",
                gap: "1rem",
                flexWrap: "wrap",
              }}
            >
              {[
                { key: "is_practice_area", label: "Practice Area" },
                { key: "is_people", label: "People" },
                { key: "is_company", label: "Company" },
              ].map((opt, index) => (
                <motion.div
                  key={opt.key}
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.3, delay: 0.2 + index * 0.1 }}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <Chip
                    label={
                      <FormControlLabel
                        value={opt.key}
                        control={
                          <Radio
                            checked={selected === opt.key}
                            onChange={handleChange}
                            value={opt.key}
                          />
                        }
                        label={opt.label}
                      />
                    }
                    variant="outlined"
                    sx={{ transition: "all 0.3s ease" }}
                  />
                </motion.div>
              ))}
            </Box>

            {/* Practice Area Autocomplete */}
            {selected === "is_practice_area" && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: "auto" }}
                exit={{ opacity: 0, height: 0 }}
                transition={{ duration: 0.4, ease: [0.25, 0.46, 0.45, 0.94] }}
              >
                <Box sx={{ mt: 2 }}>
                  <Controller
                    name="practice_area"
                    control={control}
                    render={({ field }) => (
                      <Autocomplete
                        options={allPracticeArea || []}
                        value={getCurrentPracticeArea()}
                        getOptionLabel={(option) =>
                          option?.case_type_name || option?.name || ""
                        }
                        onChange={(event, newValue) => {
                          setValue("practice_area", newValue);
                        }}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label="Practice Area"
                            size="small"
                            placeholder="Select practice area"
                          />
                        )}
                        renderOption={(props, option) => (
                          <Box component="li" {...props}>
                            <Typography variant="body2">
                              {option?.case_type_name || option?.name}
                            </Typography>
                          </Box>
                        )}
                        isOptionEqualToValue={(option, value) =>
                          option?.id === value?.id
                        }
                        fullWidth
                      />
                    )}
                  />
                </Box>
              </motion.div>
            )}
          </Box>

          {/* Task Status */}
          <Box sx={{ display: "flex", gap: "1rem" }}>
            <Controller
              name="status"
              control={control}
              render={({ field }) => (
                <FormControl size="small" fullWidth>
                  <InputLabel id="task_status-label">
                    Default Task Status
                  </InputLabel>
                  <Select
                    {...field}
                    labelId="task_status-label"
                    label="Default Task Status"
                    value={field.value || "Open"}
                  >
                    <MenuItem value="Open">Pending</MenuItem>
                    <MenuItem value="Transfered">In Progress</MenuItem>
                    <MenuItem value="Reviewed">In Review</MenuItem>
                    <MenuItem value="Completed">Complete</MenuItem>
                  </Select>
                </FormControl>
              )}
            />
          </Box>

          {/* Description */}
          <Box>
            <Controller
              name="comment"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  multiline
                  rows={3}
                  fullWidth
                  label="Template Description"
                />
              )}
            />
          </Box>

          <Divider orientation="horizontal" flexItem />

          {/* Reminders Section - Optimized */}
          <Box>
            {/* Header */}
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: "1rem",
              }}
            >
              <Box
                sx={{ display: "flex", alignItems: "center", gap: "0.5rem" }}
              >
                <Typography variant="subtitle1" fontWeight={600}>
                  Default Reminders Template
                </Typography>
                <Tooltip title="Exact: Reminder sent at precise time | Non-exact: Reminder sent at midnight (12:00 AM)">
                  <InfoOutlinedIcon
                    sx={{ fontSize: "1rem", color: "text.secondary" }}
                  />
                </Tooltip>
              </Box>

              {fields.length > 0 && (
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<AddRoundedIcon />}
                  onClick={handleAddAlarm}
                >
                  Add Reminder Template
                </Button>
              )}
            </Box>

            {/* Reminders List */}
            {fields.length > 0 ? (
              <Box
                sx={{ display: "flex", flexDirection: "column", gap: "1rem" }}
              >
                {fields.map((field, index) => (
                  <ReminderItem
                    key={field.id}
                    field={field}
                    index={index}
                    remove={remove}
                    selected={selected}
                    watch={watch}
                  />
                ))}
              </Box>
            ) : (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: "1rem",
                  my: "1rem",
                  p: "2rem",
                  border: "0.125rem dashed #e0e0e0",
                  borderRadius: "0.75rem",
                  backgroundColor: "#fafafa",
                }}
              >
                <AccessAlarmsRoundedIcon
                  sx={{
                    fontSize: "3rem",
                    color: "primary.main",
                    opacity: 0.5,
                  }}
                />
                <Box sx={{ flex: 1 }}>
                  <Typography variant="body1" sx={{ mb: "0.5rem" }}>
                    No default reminders set for this template
                  </Typography>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mb: "1rem" }}
                  >
                    Add default reminders that will be applied to tasks created
                    from this template
                  </Typography>
                  <Button
                    variant="contained"
                    size="small"
                    startIcon={<AddRoundedIcon />}
                    onClick={handleAddAlarm}
                  >
                    Add First Reminder Template
                  </Button>
                </Box>
              </Box>
            )}
          </Box>
        </Box>

        {/* Form Footer */}
        <Box
          sx={{
            height: "fit-content",
            borderTop: "1px solid #ddd",
            position: "sticky",
            bottom: 0,
            backdropFilter: "blur(10px)",
            display: "flex",
            justifyContent: "flex-end",
            alignItems: "center",
            p: "0.5rem",
            zIndex: 1000,
            gap: "0.5rem",
            bgcolor: "rgba(255, 255, 255, 0.8)",
          }}
        >
          <Button variant="outlined" size="small" onClick={closeDialog}>
            Cancel
          </Button>
          <LoadingButton
            loading={isSubmitting}
            variant="contained"
            color="primary"
            type="submit"
            size="small"
          >
            {isEditMode ? "Update Template" : "Create Template"}
          </LoadingButton>
        </Box>
      </form>
    </Box>
  );
};

export default React.memo(CreateTaskTemplateForm);
